import { currencyFormatter } from "@fla/common";
import { EditIcon, useNames } from "@fla/components";
import SplitIcon from "@fla/components/src/assets/svgs/split.svg";
import SplitV2Icon from "@fla/components/src/assets/svgs/split_2.svg";
import SuperannuationIcon from "@fla/components/src/assets/svgs/superannuation.svg";
import { Superannuation_InitialState } from "@fla/components/src/interfaces";

type Props = { superannuationData: Superannuation_InitialState[] } & ReturnType<typeof useNames>;

export function useSuper({ superannuationData, getName }: Props) {
  const getBalance = (data: any, owner: "joint" | "user" | "spouse") => {
    //if no super split
    if (data.if_split_fund !== "yes" && data.owner !== "joint") {
      return data.value;
    } else if (data.owner === "joint" && owner === "user") {
      return data.user_receive_amount;
    } else if (data.owner === "joint" && owner === "spouse") {
      return data.spouse_receive_amount;
    }
    //if super split
    else if (data.if_split_fund === "yes" && data?.if_percentage !== "yes" && data?.transfer_to === owner) {
      return data.transfer_amount;
    } else if (data.if_split_fund === "yes" && data?.if_percentage === "yes" && data?.transfer_to === owner) {
      return (Number(data.value) * Number(data.transfer_percentage)) / 100;
    } else if (data.if_split_fund === "yes" && data?.if_percentage !== "yes" && data?.transfer_to !== owner) {
      return Number(data.value) - Number(data.transfer_amount);
    } else if (data.if_split_fund === "yes" && data?.if_percentage === "yes" && data?.transfer_to !== owner) {
      return (Number(data.value) * (100 - Number(data.transfer_percentage))) / 100;
    } else return 0;
  };

  const transferredAmount = (data: any) => {
    if (data?.if_percentage === "yes") {
      return `${data.transfer_percentage}% transferred to ${getName(
        data.transfer_to
      )} (approx ${currencyFormatter.format((Number(data.value) * Number(data.transfer_percentage)) / 100)})`;
    } else
      return `${currencyFormatter.format(Number(data.transfer_amount))} transferred to ${getName(data.transfer_to)}`;
  };

  const userTotalAmount = (() => {
    return superannuationData
      .filter(sup => sup.owner === "user" || sup.owner === "joint" || sup.if_split_fund === "yes")
      .reduce((p, c) => p + Number(getBalance(c, "user")), 0);
  })();

  const spouseTotalAmount = (() => {
    return superannuationData
      .filter(sup => sup.owner === "spouse" || sup.owner === "joint" || sup.if_split_fund === "yes")
      .reduce((p, c) => p + Number(getBalance(c, "spouse")), 0);
  })();

  const getCardFor = (val: "joint" | "user" | "spouse", editData?: any) => {
    return Array.isArray(superannuationData) && superannuationData.length
      ? superannuationData
          .filter(sup => sup.owner === val || sup.owner === "joint" || sup.if_split_fund === "yes")
          .map((data, idx) => (
            <article key={idx} className="bg-white shadow-md rounded-lg p-5 mb-5">
              <section className="grid grid-cols-8 gap-y-4 md:gap-y-0 mb-2">
                <div className="col-span-2 md:col-span-1">
                  <img
                    src={data.if_split_fund === "yes" && data.transfer_to === val ? SplitV2Icon : SuperannuationIcon}
                    className="w-12 h-auto my-3"
                    alt="superannuation"
                  />
                </div>
                <div className="col-span-6 md:col-span-5">
                  {data.if_split_fund === "yes" && data.transfer_to === val ? (
                    <p className="text-lg capitalize font-semibold">
                      {data?.if_percentage === "yes"
                        ? `${data.transfer_percentage}% of ${getName(data.owner)}'s ${data.name}`
                        : `Transfer from ${getName(data.owner)}'s ${data.name}`}
                    </p>
                  ) : (
                    <p className="text-lg capitalize font-semibold">{data.name}</p>
                  )}

                  {data.trustee_smsf ? (
                    <>
                      <p className="text-sm">Trustee: {data.trustee_smsf}</p>
                      <p className="text-sm">Current Balance: {currencyFormatter.format(Number(data.value))}</p>
                    </>
                  ) : null}
                  {data.member_number ? <p className="text-sm">Member Number: {data.member_number}</p> : null}
                  <p className="text-sm">
                    Account holder:&nbsp;
                    <span className="font-medium">{getName(data.owner)}</span>
                  </p>
                  {(data?.if_split_fund === "yes" && data.transfer_to === val) ||
                  data?.if_split_fund !== "yes" ? null : (
                    <p className="text-sm">
                      Current Balance:&nbsp;
                      <span className="font-medium">{currencyFormatter.format(Number(data.value))}</span>
                    </p>
                  )}
                </div>
                <div className="col-span-8 md:col-span-2 flex flex-col items-end">
                  <p className="text-xl">{currencyFormatter.format(getBalance(data, val))}</p>
                  <p className="text-gray-500 text-sm">
                    {(data?.if_split_fund === "yes" && data.transfer_to === val) || data?.if_split_fund !== "yes"
                      ? "Balance"
                      : "Balance Remaining"}
                  </p>

                  {data?.if_split_fund === "yes" && data.transfer_to === val ? null : typeof editData === "function" ? (
                    <button
                      className="transition-all hover:bg-primary/20 px-2 py-1 rounded-md text-sm text-primary font-bold uppercase mt-4 flex items-center space-x-2"
                      onClick={() => editData(data)}
                    >
                      <EditIcon classes="w-3.5 h-3.5" />
                      <p className="tracking-wide">Edit</p>
                    </button>
                  ) : null}
                </div>
              </section>
              {data?.details && data.transfer_to !== val ? (
                <p className="line-clamp-1 pl-11 text-sm mb-3">{data.details}</p>
              ) : null}
              {data.if_split_fund === "yes" && data.transfer_to !== val ? (
                <>
                  <hr />
                  <section className="flex justify-between items-center mt-4 gap-x-5">
                    <div className="px-3 py-1 rounded-md bg-gray-300 flex items-center">
                      <img src={SplitIcon} className="w-4 h-auto mr-2" alt="superannuation" />
                      <p className="text-sm">Super Split</p>
                    </div>
                    <p className="text-sm text-gray-500">{transferredAmount(data)}</p>
                  </section>
                </>
              ) : null}

              {data.owner === "joint" ? (
                <>
                  <hr />
                  <p className="mt-4 text-sm text-gray-500">{data.joint_description}</p>
                </>
              ) : null}
            </article>
          ))
      : null;
  };
  return { getCardFor, userTotalAmount, spouseTotalAmount };
}
